/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'utils/classNamesTailwind';
import { ICON, type IconName } from './_generated/icons.registry';
import { createFigmaComponent } from './createFigmaComponent';

const iconVariants = cva('inline-block flex-shrink-0', {
  variants: {
    size: {
      xs: 'w-[10px] h-[10px]',
      sm: 'w-3 h-3',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
      xl: 'w-6 h-6',
      xxl: 'w-8 h-8',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const RawTbIcon = createFigmaComponent<IconName, VariantProps<typeof iconVariants>>(ICON, iconVariants, 'Icon');

export type TbIconProps = React.ComponentProps<typeof RawTbIcon>;
/**
 * Renders a lazy-loaded SVG TbIcon exported directly from Figma.
 * * You get full autocomplete for all available icons and sizes. It also accepts
 * standard SVG props (like `className`, `onClick`, or `aria-hidden`).
 *
 * Some icons exported from Figma hardcode a fill color on their `<path>` instead of
 * using `currentColor`. To keep `text-*` classes working consistently across all icons,
 * we force `fill: currentColor` on every `<path>`. Icons are not expected to be multicolor.
 *
 * @example
 * // Standard usage:
 * <TbIcon name="Shopping_Cart" size="md" />
 * * // With Tailwind classes and accessibility:
 * <TbIcon
 * name="Magnifying_glass"
 * size="lg"
 * className="text-gray-500 hover:text-blue-500"
 * aria-label="Open search"
 * />
 */
export const TbIcon = React.forwardRef<SVGSVGElement, TbIconProps>(({ name, className, ...props }, ref) => (
  <RawTbIcon ref={ref} name={name} className={cn('[&_path]:fill-current', className)} {...props} />
));
TbIcon.displayName = 'Icon';
